// ヤマト計算（本城さん確定ルール #31 / 2026-06-16 返信）：
//   - "宅配"   = 宅急便単価 × takkyubinQty
//       takkyubinQty = 修正前宅急便配達個数 + 修正前EAZY配達個数 + 修正後家財宅急便配達個数
//                      + 修正前宅急便集荷個数 + 修正前家財宅急便集荷個数
//   - "メール便" = ネコポス単価 × nekoposQty
//       nekoposQty   = 修正前ネコポス配完冊数 + 修正前ネコポス持戻冊数
//                      + 修正前クロネコゆうメール配完冊数 + 修正前クロネコゆうメール持戻冊数
//                      + 修正前ネコポス集荷冊数 + 修正前クロネコゆうメール集荷冊数
//   （集計式の中身は parseYamato 側で計上。本城さん「修正後に個数が乗ることは基本ないので修正前で」）
//
//   ※ 旧「その他(280円)」枠は廃止。家財配達・集荷は上記のとおり宅急便/ネコポス単価に合算する。
//   ※ 項目名は #16 でヤマト・佐川横断の共通名称に統一（宅急便・EAZY → 「宅配」、ネコポス系 → 「メール便」）。

import type { Driver, YamatoRow, Statement, ItemRow } from "../../types";
import { buildHeader, buildDeductions, eachDayOfMonth, summarize } from "./common";

export function calcYamatoStatement(
  driver: Driver,
  rows: YamatoRow[],
  targetMonth: string
): Statement {
  const days = eachDayOfMonth(targetMonth);
  const items: ItemRow[] = [];
  const warnings: string[] = [];

  // 日別集計（ドライバー1人につき1日1行が前提なので、念のため重複日も合算）
  const dailyTakkyu = new Map<string, number>();
  const dailyNekopos = new Map<string, number>();

  for (const row of rows) {
    if (!row.workDate.startsWith(targetMonth)) continue;
    dailyTakkyu.set(row.workDate, (dailyTakkyu.get(row.workDate) || 0) + row.takkyubinQty);
    dailyNekopos.set(row.workDate, (dailyNekopos.get(row.workDate) || 0) + row.nekoposQty);
  }

  const uTakkyu = driver.unitTakkyu ?? 0;
  const uNeko = driver.unitNekopos ?? 0;
  if (uTakkyu === 0) warnings.push("マスター単価/宅急便 が未設定");
  if (uNeko === 0) warnings.push("マスター単価/ネコポス が未設定");

  for (const d of days) {
    const q = dailyTakkyu.get(d) || 0;
    items.push({
      deliveryDate: d,
      name: "宅配",
      unitPrice: uTakkyu,
      quantity: q,
      amount: q * uTakkyu,
      taxRate: "10%",
      category: "sales",
      carrier: "yamato",
    });
  }
  for (const d of days) {
    const q = dailyNekopos.get(d) || 0;
    items.push({
      deliveryDate: d,
      name: "メール便",
      unitPrice: uNeko,
      quantity: q,
      amount: q * uNeko,
      taxRate: "10%",
      category: "sales",
      carrier: "yamato",
    });
  }

  const header = buildHeader(driver, ["yamato"], targetMonth);
  const deductions = buildDeductions(driver, header.salesDate);
  items.push(...deductions);

  const { salesTotal, deductTotal, netTotal } = summarize(items);

  return {
    ...header,
    items,
    salesTotal,
    deductTotal,
    netTotal,
    warnings,
    status: warnings.length > 0 ? "hold" : "draft",
  };
}
